import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Paper,
    Typography,
    Grid,
    InputAdornment,
    Alert,
    CircularProgress,
    Divider,
    Fab,
    FormHelperText,
    Chip,
    IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import { Add, Visibility, VisibilityOff } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { SmsTemplateService } from '../../../../service/smsTemplateService';
import { toast } from "react-toastify";
import useDialogModal from '../../../../hooks/useDialogModal';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import validLookup from '../../../../utils/validLookup';
import BulkSmsDialog from './BulkSmsDialog';
import { SmsService } from 'service/smsService';

// Max characters for a single SMS part (standard GSM 03.38 encoding)
const SMS_SINGLE_PART_MAX_CHARS = 160;

// Define a subset of common placeholders to display as buttons/chips
const COMMON_PLACEHOLDERS = [
    'patient_name',
    'appointment_date',
    'appointment_time',
    'clinic_name',
    'invoice_number',
];

// --- Predefined Placeholders ---
const PLACEHOLDERS = [
    { key: 'patient_name', label: 'Patient Name' },
    { key: 'appointment_date', label: 'Appointment Date' },
    { key: 'appointment_time', label: 'Appointment Time' },
    { key: 'clinic_name', label: 'Clinic Name' },
    { key: 'clinic_phone', label: 'Clinic Phone' },
    { key: 'clinic_address', label: 'Clinic Address' },
    { key: 'invoice_number', label: 'Invoice Number' },
    { key: 'invoice_amount', label: 'Invoice Amount' },
    { key: 'invoice_date', label: 'Invoice Date' },
    { key: 'invoice_balance', label: 'Invoice Balance' },
    //{ key: 'order_status', label: 'Order Status' },
];

// --- Example Data for Preview ---
const EXAMPLE_PLACEHOLDER_DATA = {
    patient_name: 'Jane Doe',
    appointment_date: 'July 15, 2025',
    appointment_time: '2:30 PM',
    clinic_name: 'OneVision Optometry',
    clinic_phone: '(555) 123-4567',
    clinic_address: '123 Main St, Anytown, USA',
    invoice_number: 'INV-2025-001',
    invoice_amount: '$250.00',
    invoice_date: new Date().toLocaleDateString('en-US'),
    invoice_balance: '$100.00',
    //order_status: 'Ready for Pickup',
};

// SMS Provider configurations
const SMS_PROVIDERS = {
    TWILIO: {
        name: 'Twilio',
        fields: [
            { name: 'accountSid', label: 'Account SID', type: 'password' },
            { name: 'authToken', label: 'Auth Token', type: 'password' },
        ]
    },
    VONAGE: {
        name: 'Vonage',
        fields: [
            { name: 'apiKey', label: 'API Key', type: 'password' },
            { name: 'apiSecret', label: 'API Secret', type: 'password' },
        ]
    }
};

export default function Sms() {
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [bulkSmsDialogOpen, setBulkSmsDialogOpen] = React.useState(false);

    const [provider, setProvider] = useState('');
    const [config, setConfig] = useState({});
    const [phoneNumber, setPhoneNumber] = useState('');
    const [monthlyLimit, setMonthlyLimit] = useState('');
    const [budgetAlert, setBudgetAlert] = useState('');
    const [testNumber, setTestNumber] = useState('');
    const [messageBody, setMessageBody] = useState(''); // This state is not directly used for currentTemplate.body

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [currentTemplate, setCurrentTemplate] = useState({
        id: null,
        templateName: '',
        header: '',
        body: '',
        footer: '',
        preference_type_required: '' // Make sure this is initialized
    });
    const [isNewTemplate, setIsNewTemplate] = useState(false);

    const [showSensitiveConfig, setShowSensitiveConfig] = useState(false);

    // Refs for text fields
    const bodyTextFieldRef = useRef(null);
    const headerTextFieldRef = useRef(null);
    const footerTextFieldRef = useRef(null);

    // NEW STATE: To track the last focused TextField ref
    const [lastFocusedTextFieldRef, setLastFocusedTextFieldRef] = useState(bodyTextFieldRef); // Default to body

    // Fetch templates and configuration on component mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const [templatesResponse, configResponse] = await Promise.all([
                    SmsTemplateService.getTemplates(),
                    SmsTemplateService.getConfig()
                ]);
                
                const fetchedTemplates = templatesResponse.data || [];
                setTemplates(fetchedTemplates);
                if (fetchedTemplates.length > 0) {
                    setSelectedTemplateId(fetchedTemplates[0].id);
                    setCurrentTemplate(fetchedTemplates[0]);
                }

                if (configResponse.data) {
                    const config = configResponse.data;
                    setProvider(config.provider || '');
                    setConfig(config.config || {});
                    setPhoneNumber(config.phoneNumber || '');
                    setMonthlyLimit(config.monthlyLimit || '');
                    setBudgetAlert(config.budgetAlert || '');
                    setTestNumber(config.testNumber || '');
                }
            } catch (error) {
                toast.error('Failed to load data: ' + error.message)
            }
        };
        loadData();
    }, []);

    // --- Template Management Handlers ---
    const handleTemplateSelect = (event) => {
        const id = event.target.value;
        setSelectedTemplateId(id);
        if (id === 'new') {
            handleNewTemplateClick();
        } else {
            const template = templates.find(t => t.id === id);
            setCurrentTemplate(template || { id: null, templateName: '', header: '', body: '', footer: '', preference_type_required: '' });
            setIsNewTemplate(false);
        }
    };

    const handleNewTemplateClick = () => {
        setSelectedTemplateId('new');
        setCurrentTemplate({ id: null, templateName: '', header: '', body: '', footer: '', preference_type_required: '' });
        setIsNewTemplate(true);
    };

    const handleTemplateFieldChange = (field, value) => {
        setCurrentTemplate(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDeleteTemplate = () => {
        if (!currentTemplate.id || isNewTemplate) {
            toast.error('Cannot delete unsaved template');
            return;
        }

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    await SmsTemplateService.deleteTemplate(currentTemplate.id);
                    setTemplates(prev => prev.filter(t => t.id !== currentTemplate.id));
                    setCurrentTemplate({ id: null, templateName: '', header: '', body: '', footer: '', preference_type_required: '' });
                    setSelectedTemplateId('');
                    setIsNewTemplate(false);
                    toast.success('Template deleted successfully!');
                } catch (error) {
                    toast.error('Failed to delete template: ' + error.message);
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete Template',
            message: `Are you sure you want to delete the template "${currentTemplate.templateName}"? This action cannot be undone.`,
        });
    };

    const handleSaveTemplate = async () => {
        if (!currentTemplate.templateName || !currentTemplate.body ) { // Added preference_type_required check
            toast.error('Template name, body, and required communication type are required.')
            return;
        }

        try {
            const templateData = {
                templateName: currentTemplate.templateName,
                header: currentTemplate.header,
                body: currentTemplate.body,
                footer: currentTemplate.footer,
                //preference_type_required: currentTemplate.preference_type_required // Include this in data to save
            };
            console.log("handleSaveTemplate: ", templateData)
            let response;
            if (isNewTemplate) {
                response = await SmsTemplateService.saveTemplate(templateData);
            } else {
                response = await SmsTemplateService.updateTemplate(currentTemplate.id, templateData);
            }

            if (!response || !response.data) {
                throw new Error('Invalid response from server');
            }

            const savedTemplate = response.data;

            // Update templates list
            if (isNewTemplate) {
                setTemplates(prev => [...prev, savedTemplate]);
            } else {
                setTemplates(prev => prev.map(t => t.id === savedTemplate.id ? savedTemplate : t));
            }
            setCurrentTemplate(savedTemplate);
            setSelectedTemplateId(savedTemplate.id);
            setIsNewTemplate(false);
            toast.success(`Template '${savedTemplate.templateName}' saved successfully!`);
        } catch (error) {
            toast.error('Failed to save template: ' + error.message);
        }
    };

    // --- Placeholder Insertion Logic ---
    const insertPlaceholder = (placeholderKey, targetRef) => {
        if (!targetRef || !targetRef.current) return;

        // Access the actual input/textarea element within the TextField's DOM structure
        const input = targetRef.current.querySelector('textarea') || targetRef.current.querySelector('input');
        if (!input) {
            console.log("No input element found for ref:", targetRef);
            return;
        }

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const placeholderText = `{${placeholderKey}}`;

        let currentFieldValue;
        let fieldToUpdate;

        // Determine which field's state to update based on the targetRef
        if (targetRef === bodyTextFieldRef) {
            currentFieldValue = currentTemplate.body;
            fieldToUpdate = 'body';
        } else if (targetRef === headerTextFieldRef) {
            currentFieldValue = currentTemplate.header;
            fieldToUpdate = 'header';
        } else if (targetRef === footerTextFieldRef) {
            currentFieldValue = currentTemplate.footer;
            fieldToUpdate = 'footer';
        } else {
            return; // Should not happen with correct refs
        }

        const newValue =
            currentFieldValue.substring(0, start) +
            placeholderText +
            currentFieldValue.substring(end);

        handleTemplateFieldChange(fieldToUpdate, newValue);

        // After state update, set cursor position (requires a small delay or direct DOM manipulation)
        setTimeout(() => {
            input.selectionStart = start + placeholderText.length;
            input.selectionEnd = start + placeholderText.length;
            input.focus(); // Re-focus the input to keep cursor visible
        }, 0);
    };

    // --- SMS Configuration Handlers ---
    const handleProviderChange = (event) => {
        const newProvider = event.target.value;
        setProvider(newProvider);
        // Don't clear config - let user manually clear if needed
        // This preserves values when switching providers or reloading
    };

    const handleConfigChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveConfig = async () => {
        try {
            const configData = {
                provider,
                config,
                phoneNumber,
                monthlyLimit,
                budgetAlert,
                testNumber
            };
            await SmsTemplateService.saveConfig(configData); // Assuming this service call exists
            toast.success('Configuration saved successfully!');
        } catch (error) {
            toast.error('Failed to save configuration: ' + error.message)
        }
    };

    const handleTestSMS = async () => {
        if (!testNumber) {
            toast.error('Please enter a test phone number.')
            return;
        }
        if (!currentTemplate.body) { // Ensure there's a message body to send
            toast.error('Please select or create a template with a message body to send a test SMS.');
            return;
        }

        try {
            // Use the currentTemplate.body for the test message
            const response = await SmsService.sendTestSms(testNumber); // Assuming this service call exists
            if (!response || !response.data) {
                throw new Error('Invalid response from server');
            }
            toast.success('Test SMS sent successfully!');
        } catch (error) {
            console.error('Error sending test SMS:', error);
            toast.error('Failed to send test SMS: ' + error.message);
        }
    };

    // Calculate SMS parts for the body
    const smsBodyParts = Math.ceil(currentTemplate.body.length / SMS_SINGLE_PART_MAX_CHARS);

    // --- Preview Logic ---
    const renderPreviewMessage = () => {
        let fullMessage = '';
        if (currentTemplate.header) {
            fullMessage += currentTemplate.header + '\n';
        }
        fullMessage += currentTemplate.body;
        if (currentTemplate.footer) {
            fullMessage += '\n' + currentTemplate.footer;
        }

        // Replace placeholders with example data
        let preview = fullMessage;
        PLACEHOLDERS.forEach(ph => {
            const placeholderKey = ph.key;
            const exampleValue = EXAMPLE_PLACEHOLDER_DATA[placeholderKey] || `[${ph.label}]`; // Fallback if no example data
            preview = preview.replace(new RegExp(`{${placeholderKey}}`, 'g'), exampleValue);
        });

        return preview;
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    SMS Configuration
                </Typography>
                <Fab
                    color="primary"
                    aria-label="bulk-sms"
                    onClick={() => setBulkSmsDialogOpen(true)}
                    title="Send SMS to multiple customers at once"
                >
                    <Add />
                </Fab>
            </Box>

            <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                <Typography variant="h5" gutterBottom>
                    SMS Configuration
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>SMS Provider</InputLabel>
                            <Select
                                value={provider}
                                onChange={handleProviderChange}
                                label="SMS Provider"
                            >
                                {Object.entries(SMS_PROVIDERS).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {provider && SMS_PROVIDERS[provider].fields.map(field => (
                        <Grid item xs={12} key={field.name}>
                            <TextField
                                fullWidth
                                label={field.label}
                                type={showSensitiveConfig ? 'text' : field.type} 
                                value={config[field.name] || ''}
                                onChange={(e) => handleConfigChange(field.name, e.target.value)}
                                InputProps={{
                                    // Add the toggle button for password type fields
                                    endAdornment: field.type === 'password' && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowSensitiveConfig(prev => !prev)}
                                                onMouseDown={(e) => e.preventDefault()} // Prevent focus shift
                                                edge="end"
                                            >
                                                {showSensitiveConfig ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}                                
                            />
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Dedicated Phone Number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Monthly SMS Limit"
                            value={monthlyLimit}
                            onChange={(e) => setMonthlyLimit(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Budget Alert ($)"
                            value={budgetAlert}
                            onChange={(e) => setBudgetAlert(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AttachMoneyIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveConfig}
                            >
                                Save Configuration
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Test Configuration
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Test Phone Number"
                                value={testNumber}
                                onChange={(e) => setTestNumber(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleTestSMS}
                                startIcon={<SendIcon />}
                            >
                                Send Test SMS
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
            <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 3 }}>

                {/* Template Management Section */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                        SMS Message Templates
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Use placeholders like {'{patient_name}'}, {'{appointment_date}'}, {'{appointment_time}'}, {'{clinic_phone}'} to personalize messages.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="template-select-label">Select Template</InputLabel>
                            <Select
                                labelId="template-select-label"
                                value={selectedTemplateId}
                                label="Select Template"
                                onChange={handleTemplateSelect}
                            >
                                <MenuItem value=""><em>-- Select a Template --</em></MenuItem>
                                {templates.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>{t.templateName}</MenuItem>
                                ))}
                                <MenuItem value="new">
                                    <Add sx={{ mr: 1 }} fontSize="small" /> Create New Template
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <Fab
                            color="primary"
                            size="small"
                            aria-label="add"
                            onClick={handleNewTemplateClick}
                            sx={{ flexShrink: 0 }}
                        >
                            <Add />
                        </Fab>
                    </Box>
                </Grid>

                {(selectedTemplateId || isNewTemplate) && ( // Show fields only if a template is selected or new
                    <>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Template Name"
                                value={currentTemplate.templateName || ''}
                                onChange={(e) => handleTemplateFieldChange('templateName', e.target.value)}
                                helperText="A unique name for this template (e.g., 'Appointment Reminder - 24hr')"
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message Header (Optional)"
                                value={currentTemplate.header}
                                onChange={(e) => handleTemplateFieldChange('header', e.target.value)}
                                helperText="Appears at the beginning of the message. Max ~50 chars. Use {clinic_name} for clinic name."
                                multiline
                                rows={1}
                                ref={headerTextFieldRef}
                                onFocus={() => setLastFocusedTextFieldRef(headerTextFieldRef)} // <--- NEW
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message Body"
                                value={currentTemplate.body}
                                onChange={(e) => handleTemplateFieldChange('body', e.target.value)}
                                helperText={`Characters: ${currentTemplate.body.length} (Parts: ${smsBodyParts}). Use placeholders like {patient_name}, {appointment_date}, {appointment_time}.`}
                                multiline
                                rows={4}
                                inputProps={{ maxLength: SMS_SINGLE_PART_MAX_CHARS * 5 }}
                                required
                                ref={bodyTextFieldRef}
                                onFocus={() => setLastFocusedTextFieldRef(bodyTextFieldRef)} // <--- NEW
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                SMS messages are typically 160 characters per part. Longer messages will be split.
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message Footer (Optional)"
                                value={currentTemplate.footer}
                                onChange={(e) => handleTemplateFieldChange('footer', e.target.value)}
                                helperText="Appears at the end. Recommended for clinic contact & CASL opt-out instructions (e.g., 'Reply STOP to unsubscribe')."
                                multiline
                                rows={2}
                                ref={footerTextFieldRef}
                                onFocus={() => setLastFocusedTextFieldRef(footerTextFieldRef)} // <--- NEW
                            />
                        </Grid>
                        {/* NEW: Placeholders Selection */}
                        <Grid item xs={12}>
                            {/* NEW: Placeholder Buttons/Chips */}
                            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {COMMON_PLACEHOLDERS.map((key) => {
                                    const placeholder = PLACEHOLDERS.find(p => p.key === key);
                                    return placeholder ? (
                                        <Chip
                                            key={key}
                                            label={placeholder.label}
                                            onClick={() => insertPlaceholder(key, lastFocusedTextFieldRef)}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ) : null;
                                })}
                            </Box>                            
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id="placeholder-select-label">Insert Placeholder</InputLabel>
                                <Select
                                    labelId="placeholder-select-label"
                                    value="" // Keep value empty so it always resets after selection
                                    label="Insert Placeholder"
                                    onChange={(e) => {
                                        // Use the last focused ref directly
                                        insertPlaceholder(e.target.value, lastFocusedTextFieldRef); // <--- CHANGE HERE
                                    }}
                                >
                                    <MenuItem value=""><em>-- Select a Placeholder --</em></MenuItem>
                                    {PLACEHOLDERS.map((ph) => (
                                        <MenuItem key={ph.key} value={ph.key}>
                                            {ph.label} (e.g., {'{'}{ph.key}{'}'})
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText sx={{ mt: 1 }}>
                                    Select a placeholder to insert it into the active text field.
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* NEW: Preference Type Required for Compliance */}
                        {/* <Grid item xs={12}>
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id="preference-type-label">Required Communication Type</InputLabel>
                                <Select
                                    labelId="preference-type-label"
                                    value={currentTemplate.preference_type_required || ''} // Ensure value is controlled
                                    label="Required Communication Type"
                                    onChange={(e) => handleTemplateFieldChange('preference_type_required', e.target.value)}
                                    required
                                >
                                    <MenuItem value=""><em>-- Select Type for Opt-in Check --</em></MenuItem> */}
                                    {/* You would fetch these dynamically from your backend's communication_preference_types */}
                                    {/* <MenuItem value="appointment_reminders">Appointment Reminders</MenuItem>
                                    <MenuItem value="order_notifications">Order Notifications</MenuItem>
                                    <MenuItem value="recall_reminders">Recall Reminders</MenuItem>
                                    <MenuItem value="marketing">Marketing/Promotions</MenuItem>
                                    <MenuItem value="billing_notifications">Billing Notifications</MenuItem>
                                </Select>
                                <FormHelperText>
                                    This template will only be sent if the customer has opted in for this specific communication type via SMS.
                                </FormHelperText>
                            </FormControl>
                        </Grid> */}

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveTemplate}
                                    disabled={(!currentTemplate.templateName || !currentTemplate.body)} // Disable if name, body, or preference type empty
                                >
                                    {currentTemplate.id ? 'Save Template' : 'Create Template'}
                                </Button>
                                {!isNewTemplate && currentTemplate.id && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleDeleteTemplate}
                                    >
                                        Delete Template
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </>
                )}
            </Paper>

            {/* NEW: SMS Preview Section */}
            {(selectedTemplateId || isNewTemplate) && (
                <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        SMS Preview
                    </Typography>
                    <Box
                        sx={{
                            border: '1px solid #ccc',
                            borderRadius: 2,
                            p: 2,
                            minHeight: 100,
                            backgroundColor: '#e0f7fa', // Light blue background for preview
                            fontFamily: 'monospace', // Monospace font to mimic phone text
                            whiteSpace: 'pre-wrap', // Preserve line breaks
                            wordBreak: 'break-word', // Break long words
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#00796b', fontWeight: 'bold' }}>
                            To: {EXAMPLE_PLACEHOLDER_DATA.patient_name} ({EXAMPLE_PLACEHOLDER_DATA.clinic_phone})
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {renderPreviewMessage()}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        This is a preview using example data. Actual message content will vary based on patient information.
                    </Typography>
                </Paper>
            )}            
            <ConfirmationDialogComp
                label={{
                    positive: "Delete",
                    negative: "Cancel",
                }} />
            
            <BulkSmsDialog
                open={bulkSmsDialogOpen}
                onClose={() => setBulkSmsDialogOpen(false)}
                onSuccess={(message) => {
                    toast.success(message);
                    setBulkSmsDialogOpen(false);
                }}
            />
        </>

    );
}